/**
 * HTML → JSX Tool
 *
 * Convert HTML to React JSX with attribute transforms.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

const ATTR_MAP: Record<string, string> = {
  class: 'className',
  for: 'htmlFor',
  tabindex: 'tabIndex',
  readonly: 'readOnly',
  maxlength: 'maxLength',
  minlength: 'minLength',
  cellpadding: 'cellPadding',
  cellspacing: 'cellSpacing',
  rowspan: 'rowSpan',
  colspan: 'colSpan',
  usemap: 'useMap',
  frameborder: 'frameBorder',
  contenteditable: 'contentEditable',
  crossorigin: 'crossOrigin',
  accesskey: 'accessKey',
  charset: 'charSet',
  datetime: 'dateTime',
  enctype: 'encType',
  formaction: 'formAction',
  formenctype: 'formEncType',
  formmethod: 'formMethod',
  formnovalidate: 'formNoValidate',
  formtarget: 'formTarget',
  hreflang: 'hrefLang',
  httpequiv: 'httpEquiv',
  'http-equiv': 'httpEquiv',
  inputmode: 'inputMode',
  srcdoc: 'srcDoc',
  srcset: 'srcSet',
  novalidate: 'noValidate',
  autofocus: 'autoFocus',
  autoplay: 'autoPlay',
  autocomplete: 'autoComplete',
};

const EVENT_MAP: Record<string, string> = {
  onclick: 'onClick',
  ondblclick: 'onDoubleClick',
  onchange: 'onChange',
  oninput: 'onInput',
  onsubmit: 'onSubmit',
  onfocus: 'onFocus',
  onblur: 'onBlur',
  onkeydown: 'onKeyDown',
  onkeyup: 'onKeyUp',
  onkeypress: 'onKeyPress',
  onmousedown: 'onMouseDown',
  onmouseup: 'onMouseUp',
  onmouseover: 'onMouseOver',
  onmouseout: 'onMouseOut',
  onmousemove: 'onMouseMove',
  onmouseenter: 'onMouseEnter',
  onmouseleave: 'onMouseLeave',
  onscroll: 'onScroll',
  onwheel: 'onWheel',
  ondrag: 'onDrag',
  ondrop: 'onDrop',
  ontouchstart: 'onTouchStart',
  ontouchend: 'onTouchEnd',
  ontouchmove: 'onTouchMove',
  onload: 'onLoad',
  onerror: 'onError',
  oncontextmenu: 'onContextMenu',
};

const SELF_CLOSING_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

export const HtmlToJsxTool: ITransformerTool = {
  id: 'html-to-jsx',
  name: 'HTML → JSX',
  description: 'Convert HTML to React JSX with proper attribute transforms',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['html', 'jsx', 'react', 'convert', 'transform', 'component'],

  seo: {
    slug: 'html-to-jsx-converter',
    pageTitle: 'HTML to JSX Converter - Free Online Tool | Prism',
    metaDescription: 'Convert HTML to React JSX instantly. Transforms class to className, for to htmlFor, inline styles to objects, and more. Fast, private, browser-based.',
    keywords: ['html to jsx', 'html to react', 'jsx converter', 'react converter', 'html jsx tool'],
    relatedTools: ['html-format', 'html-minify', 'html-entity-encode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.HTML, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'typescript',
      type: 'boolean',
      label: 'TypeScript (TSX)',
      defaultValue: false,
      description: 'Add TypeScript type annotations',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '').trim();
    if (!text) return '';
    const useTs = config.typescript === true;

    try {
      let jsx = text;

      // Convert HTML comments to JSX comments
      jsx = jsx.replace(/<!--([\s\S]*?)-->/g, '{/* $1 */}');

      // Self-closing void elements: <br> → <br />, <img ...> → <img ... />
      for (const tag of SELF_CLOSING_TAGS) {
        const re = new RegExp(`<(${tag})(\\b[^>]*?)\\s*/?>`, 'gi');
        jsx = jsx.replace(re, '<$1$2 />');
      }

      // Convert inline style strings to objects
      jsx = jsx.replace(/style="([^"]*)"/g, (_match, styleStr: string) => {
        const obj = parseStyleString(styleStr);
        return `style={${JSON.stringify(obj)}}`;
      });
      jsx = jsx.replace(/style='([^']*)'/g, (_match, styleStr: string) => {
        const obj = parseStyleString(styleStr);
        return `style={${JSON.stringify(obj)}}`;
      });

      // Convert HTML attributes to JSX (class → className, for → htmlFor, etc.)
      for (const [html, jsxAttr] of Object.entries(ATTR_MAP)) {
        const re = new RegExp(`\\b${escapeRegExp(html)}(\\s*=)`, 'g');
        jsx = jsx.replace(re, `${jsxAttr}$1`);
        // Boolean attributes without values: e.g., <input readonly> → <input readOnly>
        const reBool = new RegExp(`\\b${escapeRegExp(html)}(?=\\s|>|/>)`, 'g');
        jsx = jsx.replace(reBool, jsxAttr);
      }

      // Convert event handlers
      for (const [html, jsxEvent] of Object.entries(EVENT_MAP)) {
        const re = new RegExp(`\\b${escapeRegExp(html)}=`, 'gi');
        jsx = jsx.replace(re, `${jsxEvent}=`);
      }

      // Wrap in component if TypeScript
      if (useTs) {
        const lines = jsx.split('\n').map(l => `    ${l}`).join('\n');
        jsx = `const Component: React.FC = () => {\n  return (\n${lines}\n  );\n};\n\nexport default Component;`;
      }

      return jsx;
    } catch (e) {
      const error = e as Error;
      throw ToolError.execution(`Failed to convert HTML to JSX: ${error.message}`);
    }
  },
};

function parseStyleString(style: string): Record<string, string> {
  const obj: Record<string, string> = {};
  const declarations = style.split(';').filter(d => d.trim());

  for (const decl of declarations) {
    const colonIdx = decl.indexOf(':');
    if (colonIdx === -1) continue;

    const prop = decl.slice(0, colonIdx).trim();
    const value = decl.slice(colonIdx + 1).trim();

    // Convert CSS property to camelCase
    const camelProp = prop.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
    obj[camelProp] = value;
  }

  return obj;
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
